import { useEffect, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import bizApi, { apiError } from "@/lib/businessApi";
import { useBiz } from "@/lib/businessI18n";
import { useBusinessAuth } from "@/lib/businessAuth";
import { Badge, btnPrimary, btnSecondary, cardCls, formatChf, inputCls, labelCls } from "./ui";

export default function Commissions({ org }) {
  const { t } = useBiz();
  const { user } = useBusinessAuth();
  const canBank = (user?.permissions || []).includes("manage_billing_settings");
  const [data, setData] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [details, setDetails] = useState(null);
  const [form, setForm] = useState({ account_holder: "", legal_name: "", address: "", country: "CH", iban: "", bic: "", currency: "CHF", accounting_email: "", uid: "", vat_number: "" });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    bizApi.get("/commissions").then((r) => setData(r.data)).catch(() => setData({ commissions: [], wallet: {} }));
    bizApi.get("/commissions/payouts").then((r) => setPayouts(r.data)).catch(() => {});
    if (!canBank) return;  // coordonnées bancaires : réservées à la permission manage_billing_settings
    bizApi.get("/commissions/payout-details").then((r) => {
      setDetails(r.data);
      setForm((f) => ({ ...f, account_holder: r.data.account_holder || "", legal_name: r.data.legal_name || "",
        address: r.data.address || "", country: r.data.country || "CH", bic: r.data.bic || "",
        currency: r.data.currency || "CHF", accounting_email: r.data.accounting_email || "",
        uid: r.data.uid || "", vat_number: r.data.vat_number || "" }));
    }).catch(() => setDetails({}));
  }, []);

  const saveDetails = async () => {
    try {
      const r = await bizApi.patch("/commissions/payout-details", form);
      setDetails(r.data);
      setForm((f) => ({ ...f, account_holder: r.data.account_holder || f.account_holder,
        legal_name: r.data.legal_name || "", address: r.data.address || "",
        country: r.data.country || "CH", iban: "", bic: r.data.bic || "",
        currency: r.data.currency || "CHF", accounting_email: r.data.accounting_email || "",
        uid: r.data.uid || "", vat_number: r.data.vat_number || "" }));
      setSaved(true);
      toast.success(t("pay.saved"));
    } catch (e) {
      toast.error(apiError(e, t("pay.iban_invalid")));
    }
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-miles" /></div>;

  if (!org?.commercial?.commission_enabled) {
    return (
      <div data-testid="commissions-disabled" className={`${cardCls} mx-auto mt-10 max-w-lg p-10 text-center`}>
        <p className="text-sm text-slate">{t("comm.disabled")}</p>
      </div>
    );
  }

  const w = data.wallet || {};
  const kpis = [
    [t("comm.available"), formatChf(w.available), w.available < 0],
    [t("comm.pending"), formatChf(w.pending), false],
    [t("comm.paid_year"), formatChf(w.paid_this_year), false],
    [t("comm.lifetime"), formatChf(w.lifetime), false],
  ];

  return (
    <div data-testid="commissions-view">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight text-ink">{t("comm.title")}</h1>
        <div className="flex items-center gap-3">
          {data.rate !== null && data.rate !== undefined && (
            <p className="text-xs text-slate">{t("comm.rate")} : <span className="font-bold text-ink" data-testid="comm-rate">{data.rate} %</span></p>
          )}
          <a href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/business/commissions/export`}
            target="_blank" rel="noreferrer" className={btnSecondary} data-testid="comm-export">
            <Download className="h-3.5 w-3.5" /> {t("comm.export")}
          </a>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="comm-kpis">
        {kpis.map(([label, value, negative]) => (
          <div key={label} className={`${cardCls} p-4`}>
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate/70">{label}</p>
            <p className={`mt-1 text-lg font-bold ${negative ? "text-red-600" : "text-ink"}`}>{value}</p>
          </div>
        ))}
      </div>

      <section className={`${cardCls} mt-4 overflow-hidden`} data-testid="comm-table">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead>
              <tr className="border-b border-ink/[0.06] text-[10px] font-semibold uppercase tracking-[0.12em] text-slate/60">
                {[t("comm.date"), t("comm.booking"), t("comm.guest"), t("comm.service"), t("comm.commissionable"), t("comm.rate_applied"), t("comm.commission"), t("comm.status")].map((h) => (
                  <th key={h} className="px-4 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.commissions.length === 0 && (
                <tr><td colSpan="8" className="px-4 py-8 text-center text-xs text-slate" data-testid="comm-empty">{t("comm.empty")}</td></tr>
              )}
              {data.commissions.map((c) => (
                <tr key={c.id} className="border-b border-ink/[0.04]" data-testid={`comm-row-${c.id}`}>
                  <td className="px-4 py-3 text-xs text-slate">{(c.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 font-mono text-xs font-semibold text-ink">{c.mission_number}</td>
                  <td className="px-4 py-3 text-xs">{c.guest || "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate">{c.service ? t(`type.${c.service}`) : "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{formatChf(c.commissionable_amount)}</td>
                  <td className="px-4 py-3 font-mono text-xs">{c.rate} %</td>
                  <td className={`px-4 py-3 font-mono text-xs font-bold ${c.amount < 0 ? "text-red-600" : "text-ink"}`}>{formatChf(c.amount)}</td>
                  <td className="px-4 py-3"><Badge tone={c.status}>{t(`comm.st.${c.status}`)}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <section className={`${cardCls} p-5`} data-testid="payouts-list">
          <h2 className="text-sm font-bold text-ink">{t("comm.payouts")}</h2>
          {payouts.length === 0 ? (
            <p className="mt-2 text-xs text-slate">{t("comm.no_payouts")}</p>
          ) : (
            <div className="mt-2 space-y-2">
              {payouts.map((p) => (
                <div key={p.id} className="flex items-center gap-3 rounded-xl border border-ink/[0.06] p-3" data-testid={`payout-${p.number}`}>
                  <span className="font-mono text-xs font-bold text-ink">{p.number}</span>
                  <span className="text-xs text-slate">{p.period} · {p.count} commissions</span>
                  <span className="ml-auto font-mono text-sm font-bold text-ink">{formatChf(p.amount)}</span>
                  <Badge tone={p.status}>{p.status === "paid" ? t("comm.st.paid") : p.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className={`${cardCls} p-5`} data-testid="payout-details">
          {canBank ? (
            <>
          <h2 className="text-sm font-bold text-ink">{t("comm.payout_details")}</h2>
          {details?.iban_masked && (
            <p className="mt-1 font-mono text-xs text-slate" data-testid="iban-masked">{details.iban_masked}</p>
          )}
          {details?.payout_hold && (
            <p className="mt-2 rounded-xl bg-amber-50 px-3 py-2 text-[11px] text-amber-700" data-testid="payout-hold-warning">{t("comm.hold_warning")}</p>
          )}
          <div className="mt-3 grid grid-cols-2 gap-2">
            {[["account_holder", t("pay.holder")], ["legal_name", t("pay.legal_name")],
              ["address", t("pay.address")], ["country", t("pay.country")],
              ["iban", t("pay.iban")], ["bic", t("pay.bic")],
              ["accounting_email", t("pay.accounting_email")], ["uid", t("pay.uid")],
              ["vat_number", t("pay.vat")]].map(([k, lab]) => (
              <div key={k}>
                <label className={labelCls}>{lab}</label>
                <input className={`${inputCls} h-10`} value={form[k]} data-testid={`payout-${k}`}
                  onChange={(e) => { setForm({ ...form, [k]: e.target.value }); setSaved(false); }} />
              </div>
            ))}
          </div>
          <button type="button" onClick={saveDetails} className={`${btnPrimary} mt-4 w-full`} data-testid="payout-save">
            {t("pay.save")}
          </button>
          {saved && <p className="mt-2 text-[11px] text-emerald-600">{t("pay.saved")}</p>}
            </>
          ) : (
            <p className="text-xs text-slate">{t("comm.bank_restricted")}</p>
          )}
        </section>
      </div>
    </div>
  );
}
